import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Typography, Grid, Paper, Box, Grow, Zoom } from '@mui/material';
import { Home, Kitchen, Bathtub, Settings, Check } from '@mui/icons-material';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(4),
		marginTop: theme.spacing(3),
		borderRadius: '16px',
		backgroundImage: 'linear-gradient(120deg, #ffffff 0%, #f5f7fa 100%)',
		boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
		overflow: 'hidden',
		position: 'relative',
		'&::before': {
			content: '""',
			position: 'absolute',
			top: 0,
			left: 0,
			width: '100%',
			height: '6px',
			background: 'linear-gradient(90deg, #ff4d4d, #ff9999)'
		}
	},
	title: {
		fontWeight: 800,
		marginBottom: theme.spacing(2),
		textAlign: 'center',
		color: '#2d3748',
		fontSize: '2rem',
		letterSpacing: '0.5px',
		textTransform: 'uppercase',
		background: 'linear-gradient(90deg, #2d3748 0%, #4a5568 100%)',
		WebkitBackgroundClip: 'text',
		WebkitTextFillColor: 'transparent'
	},
	introduction: {
		color: '#e53e3e',
		textAlign: 'center',
		marginBottom: theme.spacing(4),
		fontWeight: 500,
		fontSize: '0.8rem',
		lineHeight: 1.5,
		[theme.breakpoints.down('sm')]: {
			fontSize: '0.75rem'
		}
	},
	footer: {
		color: '#e53e3e',
		textAlign: 'center',
		marginTop: theme.spacing(4),
		fontWeight: 500,
		fontSize: '0.8rem',
		lineHeight: 1.5,
		[theme.breakpoints.down('sm')]: {
			fontSize: '0.75rem'
		}
	},
	sectionTitle: {
		fontWeight: 700,
		marginBottom: theme.spacing(2),
		textAlign: 'center',
		color: '#2d3748',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		'& svg': {
			marginRight: theme.spacing(1),
			color: '#e53e3e'
		}
	},
	tipList: {
		margin: 0,
		padding: 0,
		listStyleType: 'none'
	},
	tipItem: {
		marginBottom: theme.spacing(1.5),
		fontSize: '0.95rem',
		color: '#4a5568',
		display: 'flex',
		alignItems: 'center',
		'& svg': {
			marginRight: theme.spacing(1),
			color: '#38a169',
			fontSize: '1rem'
		},
		[theme.breakpoints.down('sm')]: {
			fontSize: '0.9rem'
		}
	},
	sectionContainer: {
		marginBottom: theme.spacing(3)
	},
	sectionPaper: {
		padding: theme.spacing(3),
		height: '100%',
		borderRadius: '12px',
		boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
		transition: 'transform 0.3s ease, box-shadow 0.3s ease',
		'&:hover': {
			transform: 'translateY(-5px)',
			boxShadow: '0 12px 20px rgba(0, 0, 0, 0.1)'
		}
	}
}));

const LeadShowingTips = () => {
	const classes = useStyles();

	const tips = {
		exterior: [
			"Don't restrict access.",
			'Cut grass and remove all weeds.',
			'Straighten deck furniture.',
			'Remove or coil garden hose neatly.',
			'Place garbage bins out of sight.',
			'Remove seasonal decor.'
		],
		kitchens: [
			'Clean all counter tops.',
			'Remove dishes from the sink.',
			'Remove dish drying racks.',
			'Clean the refrigerator (Front, sides and top).',
			'Remove trash cans.',
			'Remove floor mats.'
		],
		general: [
			'Replace any burned out bulbs.',
			'Conceal cords.',
			'Remove small rugs from floors.',
			'Make beds and close closets.',
			'Remove clutter from all rooms.',
			'Address pet odors.',
			'Deodorize & neutralize.'
		],
		bathrooms: [
			'Clean the countertops.',
			'Remove toilet cleaning brushes.',
			'Remove plungers and trash cans.',
			'Toilet lids must be down.',
			'Remove all floor mats.',
			'Ensure towels are neatly hung.'
		]
	};

	return (
		<Zoom in={true} timeout={500}>
			<Paper className={classes.root} elevation={3}>
				<Typography variant="h4" className={classes.title}>
					TIPS FOR BEST SHOWING
				</Typography>

				<Typography variant="body1" className={classes.introduction}>
					Buyers are excited to see your home. They have high hopes that this will be the
					one! Everything you do to help bring that vision to a reality will benefit you
					in the long run.
				</Typography>

				<Grid container spacing={3}>
					<Grid item xs={12} sm={6} className={classes.sectionContainer}>
						<Grow in={true} timeout={800}>
							<Paper className={classes.sectionPaper}>
								<Typography variant="h6" className={classes.sectionTitle}>
									<Home /> EXTERIOR
								</Typography>
								<ul className={classes.tipList}>
									{tips.exterior.map((tip, index) => (
										<li key={`exterior-${index}`} className={classes.tipItem}>
											<Check /> {tip}
										</li>
									))}
								</ul>
							</Paper>
						</Grow>
					</Grid>

					<Grid item xs={12} sm={6} className={classes.sectionContainer}>
						<Grow in={true} timeout={1000}>
							<Paper className={classes.sectionPaper}>
								<Typography variant="h6" className={classes.sectionTitle}>
									<Kitchen /> KITCHENS
								</Typography>
								<ul className={classes.tipList}>
									{tips.kitchens.map((tip, index) => (
										<li key={`kitchen-${index}`} className={classes.tipItem}>
											<Check /> {tip}
										</li>
									))}
								</ul>
							</Paper>
						</Grow>
					</Grid>

					<Grid item xs={12} sm={6} className={classes.sectionContainer}>
						<Grow in={true} timeout={1200}>
							<Paper className={classes.sectionPaper}>
								<Typography variant="h6" className={classes.sectionTitle}>
									<Settings /> GENERAL
								</Typography>
								<ul className={classes.tipList}>
									{tips.general.map((tip, index) => (
										<li key={`general-${index}`} className={classes.tipItem}>
											<Check /> {tip}
										</li>
									))}
								</ul>
							</Paper>
						</Grow>
					</Grid>

					<Grid item xs={12} sm={6} className={classes.sectionContainer}>
						<Grow in={true} timeout={1400}>
							<Paper className={classes.sectionPaper}>
								<Typography variant="h6" className={classes.sectionTitle}>
									<Bathtub /> BATHROOMS
								</Typography>
								<ul className={classes.tipList}>
									{tips.bathrooms.map((tip, index) => (
										<li key={`bathroom-${index}`} className={classes.tipItem}>
											<Check /> {tip}
										</li>
									))}
								</ul>
							</Paper>
						</Grow>
					</Grid>
				</Grid>

				<Typography variant="body1" className={classes.footer}>
					Store all removed items in the garage, closets, cabinets or pick one room in
					your home to store all the removed items.
				</Typography>
			</Paper>
		</Zoom>
	);
};

export default LeadShowingTips;
